"use client";

import { ListingGalleryFallback } from "@/components/listings/gallery/listing-gallery-fallback";
import {
  ListingGalleryLightbox,
  type ListingGalleryLightboxHandle,
} from "@/components/listings/gallery/listing-gallery-lightbox";
import { ListingGalleryPhotosOnRequest } from "@/components/listings/gallery/listing-gallery-photos-on-request";
import { ListingEditButton } from "@/components/listings/listing-edit-button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import type { DetailedListing } from "@/lib/types";
import { buildListingGalleryUrl } from "@/lib/url";
import { cn } from "@/lib/utils";
import {
  type ImageTransformationKind,
  buildImageCDNUrl,
} from "@repo/image-transformations";
import { useTranslations } from "next-intl";
import Image from "@/components/common/image";
import Link from "next/link";
import { useMemo, useRef } from "react";

export type ListingGalleryProps = {
  listing: DetailedListing;
  inline?: boolean;
};

export function ListingGallery({ listing, inline }: ListingGalleryProps) {
  const t = useTranslations();

  const isSold = listing.status === "SOLD";
  const imageKind: ImageTransformationKind = isSold
    ? "largeblurred"
    : "large";

  const lightboxRef = useRef<ListingGalleryLightboxHandle>(null);

  function handleLightboxOpen(media?: DetailedListing["listingMedia"][number]) {
    if (!media || isSold) {
      return;
    }

    lightboxRef.current?.open(media);
  }

  const gallery = useMemo(() => {
    const output: {
      items: Array<{
        image: DetailedListing["listingMedia"][number]["image"];
        media?: DetailedListing["listingMedia"][number];
      }>;
      usedPlaceholder: boolean;
    } = {
      items: listing.listingMedia.map((m) => ({
        image: m.image,
        media: m,
      })),
      usedPlaceholder: false,
    };

    if (output.items.length > 0) {
      return output;
    }

    if (listing.placeholderImage) {
      output.usedPlaceholder = true;
      output.items = [
        {
          image: listing.placeholderImage,
        },
      ];
    }

    return output;
  }, [listing]);

  return (
    <section className={cn("relative", { "mb-4": !inline })}>
      {gallery.items.length === 0 && (
        <ListingGalleryFallback listing={listing} />
      )}

      {gallery.items.length > 0 && (
        <>
          <div className={cn(
            "md:hidden -mx-4 px-4 flex gap-2.5 pb-1",
            gallery.items.length > 1 && "overflow-x-auto snap-x snap-mandatory",
          )}>
            {gallery.items.slice(0, 5).map((item, idx) => {
              const visibleSlice = gallery.items.slice(0, 5);
              const isLastVisible = idx === visibleSlice.length - 1;
              const showOverlay =
                isLastVisible &&
                gallery.items.length > visibleSlice.length &&
                !gallery.usedPlaceholder;

              return (
                <div
                  key={`mobile-${idx}`}
                  className={cn(
                    "relative h-[200px] snap-start overflow-hidden rounded-xl",
                    gallery.items.length === 1 ? "min-w-full" : "min-w-[82%]",
                    {
                      "cursor-pointer": !gallery.usedPlaceholder && !showOverlay,
                    },
                  )}
                  onClick={() =>
                    showOverlay ? undefined : handleLightboxOpen(item.media)
                  }
                  onKeyUp={(e) => {
                    if (e.key === "Enter" && !showOverlay) {
                      handleLightboxOpen(item.media);
                    }
                  }}
                >
                  {item.image && (
                    <Image
                      src={buildImageCDNUrl(item.image.file.key, imageKind)}
                      alt={listing.pitch ?? ""}
                      fill
                      className="w-full h-full object-cover"
                    />
                  )}

                  {showOverlay && !isSold && (
                    <Link
                      href={buildListingGalleryUrl(listing)}
                      className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-semibold text-sm"
                    >
                      {t("saas.listing.photos.seeAll", {
                        count: gallery.items.length,
                      })}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>

          {gallery.items.length === 1 && (
            <div
              className={cn(
                "hidden md:block relative rounded-xl overflow-hidden",
                inline ? "h-[260px]" : "h-[280px] md:h-[388px]",
                { "cursor-pointer": !gallery.usedPlaceholder },
              )}
              onClick={() =>
                !gallery.usedPlaceholder
                  ? handleLightboxOpen(gallery.items[0]?.media)
                  : undefined
              }
              onKeyUp={(e) => {
                if (e.key === "Enter" && !gallery.usedPlaceholder) {
                  handleLightboxOpen(gallery.items[0]?.media);
                }
              }}
            >
              {gallery.items[0]?.image && (
                <Image
                  src={buildImageCDNUrl(
                    gallery.items[0].image.file.key,
                    imageKind,
                  )}
                  alt={listing.pitch ?? ""}
                  fill
                  className="w-full h-full object-cover"
                />
              )}
            </div>
          )}

          {gallery.items.length === 2 && (
            <div className="hidden md:grid grid-cols-2 gap-4 relative">
              {gallery.items.map((item, idx) => (
                <div
                  key={`desktop-${idx}`}
                  className={cn("relative", {
                    "cursor-pointer": !gallery.usedPlaceholder,
                  })}
                  onClick={() => handleLightboxOpen(item.media)}
                  onKeyUp={(e) => {
                    if (e.key === "Enter") {
                      handleLightboxOpen(item.media);
                    }
                  }}
                >
                  <AspectRatio
                    ratio={16 / 9}
                    className="rounded-xl overflow-hidden"
                  >
                    {item.image && (
                      <Image
                        src={buildImageCDNUrl(item.image.file.key, imageKind)}
                        alt={listing.pitch ?? ""}
                        fill
                        className="w-full h-full object-cover"
                      />
                    )}
                  </AspectRatio>
                </div>
              ))}
            </div>
          )}

          {gallery.items.length >= 3 && (
            <div
              className={cn(
                "hidden md:grid grid-cols-3 grid-rows-3 md:grid-rows-none md:grid-cols-listing-gallery gap-4 relative",
                {
                  "grid-cols-2": gallery.items.length === 3,
                },
              )}
            >
              {gallery.items.slice(0, 5).map((item, idx) => {
                const visibleSlice = gallery.items.slice(0, 5);
                const isLastVisible = idx === visibleSlice.length - 1;
                const showOverlay =
                  isLastVisible &&
                  gallery.items.length > visibleSlice.length &&
                  !gallery.usedPlaceholder;

                return (
                  <div
                    key={`desktop-${idx}`}
                    className={cn("relative overflow-hidden rounded-xl", {
                      "cursor-pointer": !gallery.usedPlaceholder,
                      "col-span-3 row-span-2 md:row-span-2 md:col-auto":
                        idx === 0,
                      "col-span-2 md:col-auto":
                        idx === 0 && gallery.items.length === 3,
                      "h-[120px] md:h-[188px]": idx > 0,
                      "hidden md:block": idx > 3,
                      "row-span-2 h-[240px] md:h-[376px]":
                        idx > 0 && gallery.items.length === 3,
                      "md:col-span-2":
                        idx === 3 && gallery.items.length === 4,
                    })}
                    onClick={() =>
                      showOverlay ? undefined : handleLightboxOpen(item.media)
                    }
                    onKeyUp={(e) => {
                      if (e.key === "Enter" && !showOverlay) {
                        handleLightboxOpen(item.media);
                      }
                    }}
                  >
                    {item.image && (
                      <Image
                        src={buildImageCDNUrl(item.image.file.key, imageKind)}
                        alt={listing.pitch ?? ""}
                        fill
                        className="w-full h-full object-cover"
                      />
                    )}

                    {showOverlay && !isSold && (
                      <Link
                        href={buildListingGalleryUrl(listing)}
                        className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-semibold text-sm hover:bg-black/60 transition-colors"
                      >
                        {t("saas.listing.photos.seeAll", {
                          count: gallery.items.length,
                        })}
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {gallery.items.length > 0 &&
        !gallery.usedPlaceholder &&
        gallery.items.length <= 5 &&
        !isSold && (
          <Button variant="outline" asChild className="hidden md:inline-flex absolute bottom-4 right-4">
            <Link href={buildListingGalleryUrl(listing)}>
              {t("saas.listing.photos.seeAll", {
                count: gallery.items.length,
              })}
            </Link>
          </Button>
        )}

      {isSold && gallery.items.length > 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="bg-black/60 text-white text-sm font-semibold px-4 py-2 rounded-lg uppercase tracking-wide">
            Sold
          </span>
        </div>
      )}

      <ListingEditButton
        listing={listing}
        step="PHOTOS"
        label={t(
          listing.listingMedia.length > 0
            ? "saas.listing.photos.edit"
            : "saas.listing.photos.add",
        )}
        icon={listing.listingMedia.length > 0 ? "Pencil2" : "Plus"}
        className="hidden md:block absolute top-4 right-4"
      />

      <ListingGalleryLightbox ref={lightboxRef} listing={listing} />

      {gallery.usedPlaceholder && (
        <ListingGalleryPhotosOnRequest listing={listing} />
      )}
    </section>
  );
}
